/**
 * HandoverForm — le formulaire de passation (prise ou retour).
 *
 * Retour JGS (test Chrome 2026-09-13) : « le kilométrage est demandé, il
 * devrait être préaffiché avec validation de la part du conducteur ». Le
 * serveur sait déjà une valeur avant la prise (`checkout-prefill` →
 * `odometer.km` + `odometer.source`, `backend/modules/pool/README.md`) ; à la
 * restitution la même donnée vient de l'accueil (`active_custody.odometer`).
 * On l'affiche, on demande un geste explicite — « c'est bien ça » ou
 * « corriger » — jamais une resaisie silencieuse.
 *
 * ⚠️ CARBURANT — LIMITE ASSUMÉE : le module ne sert AUCUNE valeur de niveau
 * de carburant connue à ce jour (ni dans `checkout-prefill`, ni dans
 * l'accueil) — seule l'ÉCRITURE existe côté serveur (`fuel_level_pct` en
 * entrée de `checkout`/`checkin`, jamais en sortie). Fabriquer une valeur ou
 * une « source » ici serait mentir sur ce que l'app sait vraiment : la saisie
 * reste donc directe, avec un indice qui le dit plutôt qu'un silence pris
 * pour un oubli.
 *
 * LA PASSATION EN UNE QUESTION (repris de l'app Android, validé JGS 2026-09-14)
 *
 * L'ordre à l'écran n'est pas décoratif : ce que le véhicule porte DÉJÀ est
 * montré AVANT la question. Un conducteur qui ne voit pas la rayure connue la
 * re-signale, et le gestionnaire reçoit trois fois le même défaut. Puis un seul
 * geste par défaut — « Rien de nouveau » — et un geste secondaire pour celui
 * qui a quelque chose à dire.
 *
 * ⚠️ VOCABULAIRE FERMÉ, SERVI PAR LE SERVEUR (`prefill.vocabulary`) : zones,
 * natures de dégât et gravités ne sont JAMAIS recopiées ici. Le serveur refuse
 * un code qu'il ne connaît pas (`validateDefect`), et une liste locale
 * divergerait en silence. Si le prefill n'apporte aucun vocabulaire, on
 * n'invente pas de codes : la remarque libre part en `notes`, avec
 * `no_new_defect: false` — un fait signalé, pas un défaut mal rangé.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Gauge, Fuel, Check, Pencil, AlertTriangle, ShieldAlert } from 'lucide-react';
import { isFlagOn } from '../../../utils/dbFlags.js';

/** Sources dont la valeur peut être montrée telle quelle, en confirmation. */
const KNOWN_ODOMETER_SOURCES = ['telematics', 'shadow', 'reading', 'vehicle_mileage'];

function minutesAgo(at, now = new Date()) {
  if (!at) return null;
  return Math.max(0, Math.round((now - new Date(at)) / 60000));
}

/**
 * Le compteur : trois états — la proposition à confirmer/corriger, confirmée
 * (verrouillée), ou en saisie libre (rien à confirmer, ou correction en cours).
 */
function OdometerField({ known, previousKm, value, onChange, step, onConfirm, onCorrect, onEditAgain, t, fmt, formatDuration }) {
  const hasKnown = known?.km != null && KNOWN_ODOMETER_SOURCES.includes(known?.source);
  const ago = minutesAgo(known?.at);
  const sourceLabel = t(
    `poolDriver.handover.odometerSource.${known?.source || 'unknown'}`,
    ago != null ? { duration: formatDuration(ago) } : undefined
  );
  const regressed = previousKm != null && value !== '' && Number(value) < previousKm;

  return (
    <div>
      <span className="mb-1 flex items-center gap-1.5 text-sm font-medium text-gray-700">
        <Gauge className="w-4 h-4" /> {t('poolDriver.handover.odometer')}
      </span>

      {step === 'propose' && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-2xl font-bold text-gray-900">{fmt.number(known.km)} km</p>
          <p className="mt-0.5 text-xs text-gray-500">{sourceLabel}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onConfirm}
              className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
            >
              <Check className="w-4 h-4" /> {t('poolDriver.handover.odometerConfirm')}
            </button>
            <button
              type="button"
              onClick={onCorrect}
              className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              <Pencil className="w-4 h-4" /> {t('poolDriver.handover.odometerCorrect')}
            </button>
          </div>
        </div>
      )}

      {step === 'confirmed' && (
        <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
          <p className="flex items-center gap-2 font-semibold text-emerald-900">
            <Check className="h-4 w-4 shrink-0" /> {fmt.number(known.km)} km
          </p>
          <button type="button" onClick={onEditAgain} className="text-sm text-emerald-700 underline underline-offset-2 hover:text-emerald-900">
            {t('poolDriver.handover.odometerCorrect')}
          </button>
        </div>
      )}

      {step === 'edit' && (
        <>
          <input
            type="number"
            inputMode="numeric"
            autoFocus={hasKnown}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
          <span className="mt-1 block text-xs text-gray-500">{sourceLabel}</span>
          {regressed && (
            <p className="mt-1.5 flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {t('poolDriver.handover.odometerRegressionWarning', { previous: fmt.number(previousKm) })}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Jauge de carburant à crans — retour JGS (test Chrome 2026-09-13) : « si tu
 * laisses le conducteur saisir une valeur il va mettre n'importe quoi ». Une
 * jauge de tableau de bord se lit au quart : 0, 25, 50, 75, 100, rien d'autre.
 * Tant que le conducteur n'a pas touché la jauge, rien n'est déclaré (`''` →
 * `fuel_level_pct: null`) : un curseur posé par défaut au milieu serait une
 * valeur inventée, prise pour une lecture.
 */
const FUEL_STEPS = [0, 25, 50, 75, 100];

function FuelGauge({ value, onChange, t }) {
  const declared = value !== '';
  const pick = (raw) => onChange(String(FUEL_STEPS.reduce((best, s) => (
    Math.abs(s - Number(raw)) < Math.abs(best - Number(raw)) ? s : best
  ), FUEL_STEPS[0])));

  return (
    <div>
      <span id="fuel-gauge-label" className="mb-1 flex items-center justify-between gap-1.5 text-sm font-medium text-gray-700">
        <span className="flex items-center gap-1.5">
          <Fuel className="w-4 h-4" /> {t('poolDriver.handover.fuel')}
        </span>
        <span className={declared ? 'font-semibold text-gray-900' : 'text-xs font-normal text-gray-500'}>
          {declared ? t(`poolDriver.handover.fuelSteps.${value}`) : t('poolDriver.handover.fuelNotSet')}
        </span>
      </span>
      <input
        type="range"
        min="0"
        max="100"
        step="25"
        value={declared ? value : 50}
        aria-labelledby="fuel-gauge-label"
        aria-valuetext={declared ? t(`poolDriver.handover.fuelSteps.${value}`) : t('poolDriver.handover.fuelNotSet')}
        onChange={(e) => pick(e.target.value)}
        // Un clic sur le cran déjà sous le curseur ne déclenche pas `change` :
        // sans ceci, « la moitié » ne se déclare jamais tant que la jauge n'a pas bougé.
        onPointerUp={(e) => pick(e.currentTarget.value)}
        className={`w-full accent-emerald-600 ${declared ? '' : 'opacity-50'}`}
      />
      <div className="mt-1 grid grid-cols-5 text-center">
        {FUEL_STEPS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => pick(s)}
            aria-pressed={value === String(s)}
            className={`min-h-[36px] rounded text-xs ${value === String(s) ? 'font-semibold text-emerald-700' : 'text-gray-500 hover:text-gray-800'}`}
          >
            {t(`poolDriver.handover.fuelSteps.${s}`)}
          </button>
        ))}
      </div>
      {/* Limite assumée : rien de connu à préafficher (cf. en-tête du fichier). */}
      <span className="mt-1 block text-xs text-gray-500">{t('poolDriver.handover.fuelUnknown')}</span>
    </div>
  );
}

/**
 * Ce que le véhicule porte DÉJÀ — montré avant la question, jamais après.
 * Trois défauts au plus : devant un véhicule, à 7 h, personne ne lit une liste.
 */
function KnownDefects({ defects, t, fmt }) {
  const [expanded, setExpanded] = useState(false);
  if (!defects.length) return null;
  const shown = expanded ? defects : defects.slice(0, 3);
  const hidden = defects.length - shown.length;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
      <p className="flex items-center gap-1.5 text-sm font-medium text-amber-900">
        <ShieldAlert className="h-4 w-4 shrink-0" />
        {t('poolDriver.handover.knownDefectsTitle')}
      </p>
      <ul className="mt-1.5 space-y-1 text-sm text-amber-900">
        {shown.map((d, i) => (
          <li key={d.id ?? i} className="flex flex-wrap items-baseline gap-x-1.5">
            <span className="font-medium">{[d.zone, d.damage_type_label || d.damage_type].filter(Boolean).join(' — ')}</span>
            {d.description && <span className="text-amber-800">{d.description}</span>}
            {isFlagOn(d.blocking) && (
              <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-800">
                {t('poolDriver.handover.knownDefectsBlocking')}
              </span>
            )}
            {d.reported_at && <span className="text-xs text-amber-700">{fmt.date(d.reported_at)}</span>}
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-1.5 text-sm text-amber-900 underline underline-offset-2"
        >
          {t('poolDriver.handover.knownDefectsMore', { count: hidden })}
        </button>
      )}
      <p className="mt-1.5 text-xs text-amber-800">{t('poolDriver.handover.knownDefectsHint')}</p>
    </div>
  );
}

/**
 * La déclaration d'un défaut, à vocabulaire fermé. Les listes viennent du
 * serveur ; sans elles, une remarque libre — et on le dit.
 */
function DefectPicker({ vocabulary, value, onChange, t }) {
  const lists = vocabulary || {};
  const closed = Array.isArray(lists.zones) && lists.zones.length > 0
    && Array.isArray(lists.damage_types) && lists.damage_types.length > 0;
  const set = (patch) => onChange({ ...value, ...patch });

  if (!closed) {
    return (
      <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
        <p className="text-xs text-gray-600">{t('poolDriver.handover.defectVocabularyMissing')}</p>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700">{t('poolDriver.handover.defectDescription')}</span>
          <textarea
            rows={2}
            value={value.description}
            onChange={(e) => set({ description: e.target.value })}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
        </label>
      </div>
    );
  }

  const severities = Array.isArray(lists.severities) && lists.severities.length
    ? lists.severities
    : [{ code: 'minor', label: 'minor' }];

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-gray-700">{t('poolDriver.handover.defectZone')}</span>
        <select
          value={value.zone_code}
          onChange={(e) => set({ zone_code: e.target.value })}
          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
        >
          <option value="">{t('poolDriver.handover.defectChoose')}</option>
          {lists.zones.map((z) => <option key={z.code} value={z.code}>{z.label}</option>)}
        </select>
      </label>

      <label className="block">
        <span className="mb-1 block text-sm font-medium text-gray-700">{t('poolDriver.handover.defectDamageType')}</span>
        <select
          value={value.damage_type}
          onChange={(e) => set({ damage_type: e.target.value })}
          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
        >
          <option value="">{t('poolDriver.handover.defectChoose')}</option>
          {lists.damage_types.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
        </select>
      </label>

      <fieldset>
        <legend className="mb-1 text-sm font-medium text-gray-700">{t('poolDriver.handover.defectSeverity')}</legend>
        <div className="flex flex-wrap gap-2">
          {severities.map((s) => (
            <button
              key={s.code}
              type="button"
              aria-pressed={value.severity === s.code}
              onClick={() => set({ severity: s.code })}
              className={`min-h-[40px] rounded-lg border px-3 py-2 text-sm ${
                value.severity === s.code
                  ? 'border-emerald-600 bg-emerald-50 font-semibold text-emerald-800'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </fieldset>

      <label className="flex items-start gap-2 text-sm text-gray-800">
        <input
          type="checkbox"
          checked={value.blocking}
          onChange={(e) => set({ blocking: e.target.checked })}
          className="mt-0.5 h-5 w-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
        />
        <span>
          {t('poolDriver.handover.defectBlocking')}
          <span className="mt-0.5 block text-xs text-gray-500">{t('poolDriver.handover.defectBlockingHint')}</span>
        </span>
      </label>

      <label className="block">
        <span className="mb-1 block text-sm font-medium text-gray-700">
          {t(needsDescription(value) ? 'poolDriver.handover.defectDescriptionRequired' : 'poolDriver.handover.defectDescription')}
        </span>
        <textarea
          rows={2}
          value={value.description}
          onChange={(e) => set({ description: e.target.value })}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
        />
      </label>
    </div>
  );
}

/** Le joker « Autre » exige une précision — c'est la règle du serveur, pas la nôtre. */
const needsDescription = (d) => d.zone_code === 'other' || d.damage_type === 'other';

const EMPTY_DEFECT = { zone_code: '', damage_type: '', severity: 'minor', blocking: false, description: '' };

function HandoverForm({ mode, prefill, previousOdometerKm = null, pending, onSubmit, onCancel, t, fmt, formatDuration, RefusalBanner }) {
  const [known, setKnown] = useState(prefill?.odometer || null);
  const odometerTouched = useRef(false);
  const hasKnown = known?.km != null && KNOWN_ODOMETER_SOURCES.includes(known?.source);

  const [odometer, setOdometer] = useState(hasKnown ? String(known.km) : '');
  const [odometerStep, setOdometerStep] = useState(hasKnown ? 'propose' : 'edit');
  const [fuel, setFuel] = useState('');
  const [reporting, setReporting] = useState(false);
  const [defect, setDefect] = useState(EMPTY_DEFECT);
  const [notes, setNotes] = useState('');

  const openDefects = useMemo(() => prefill?.open_defects || [], [prefill?.open_defects]);
  const vocabulary = prefill?.vocabulary || null;
  const closedVocabulary = Boolean(vocabulary?.zones?.length && vocabulary?.damage_types?.length);
  // Un défaut incomplet ne part pas : le serveur le refuserait, et un refus
  // après coup fait perdre au conducteur tout ce qu'il vient de saisir.
  const defectReady = closedVocabulary
    ? Boolean(defect.zone_code && defect.damage_type && defect.severity
      && (!needsDescription(defect) || defect.description.trim()))
    : Boolean(defect.description.trim());
  const odometerReady = odometerStep !== 'propose' && odometer !== '';
  const blocked = !odometerReady || (reporting && !defectReady);

  // Le préremplissage arrive après l'ouverture du formulaire. Une réponse
  // tardive peut proposer une valeur, mais ne doit jamais écraser une saisie.
  useEffect(() => {
    if (odometerTouched.current) return;
    const next = prefill?.odometer || null;
    const available = next?.km != null && KNOWN_ODOMETER_SOURCES.includes(next?.source);
    setKnown(next);
    setOdometer(available ? String(next.km) : '');
    setOdometerStep(available ? 'propose' : 'edit');
  }, [prefill?.odometer]);

  const submit = (e) => {
    e.preventDefault();
    if (blocked) return;
    // Sans vocabulaire fermé on ne fabrique AUCUN code : la remarque part en
    // note, et `no_new_defect` reste faux — quelque chose a été signalé.
    const structured = reporting && closedVocabulary;
    const freeText = reporting && !closedVocabulary ? defect.description.trim() : '';
    onSubmit({
      odometer_km: odometer === '' ? null : Number(odometer),
      fuel_level_pct: fuel === '' ? null : Number(fuel),
      no_new_defect: !reporting,
      defects: structured
        ? [{
          zone_code: defect.zone_code,
          damage_type: defect.damage_type,
          severity: defect.severity,
          blocking: defect.blocking,
          description: defect.description.trim() || undefined,
        }]
        : undefined,
      notes: [notes, freeText].filter(Boolean).join(' — ') || undefined,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <h3 className="text-base font-semibold text-gray-900">
        {t(mode === 'checkout' ? 'poolDriver.handover.checkoutTitle' : 'poolDriver.handover.checkinTitle')}
      </h3>

      {RefusalBanner}

      <OdometerField
        known={known}
        previousKm={previousOdometerKm}
        value={odometer}
        onChange={(value) => { odometerTouched.current = true; setOdometer(value); }}
        step={odometerStep}
        onConfirm={() => { odometerTouched.current = true; setOdometerStep('confirmed'); }}
        onCorrect={() => { odometerTouched.current = true; setOdometerStep('edit'); }}
        onEditAgain={() => { odometerTouched.current = true; setOdometerStep('edit'); }}
        t={t}
        fmt={fmt}
        formatDuration={formatDuration}
      />

      <FuelGauge value={fuel} onChange={setFuel} t={t} />

      {/* Ce qui est DÉJÀ connu, avant la question : on ne fait pas re-signaler
          au conducteur ce que le gestionnaire a déjà sur son écran. */}
      <KnownDefects defects={openDefects} t={t} fmt={fmt} />

      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">{t('poolDriver.handover.defectQuestion')}</p>
        {reporting && (
          <DefectPicker vocabulary={vocabulary} value={defect} onChange={setDefect} t={t} />
        )}
      </div>

      <label className="block">
        <span className="mb-1 block text-sm font-medium text-gray-700">{t('poolDriver.handover.notes')}</span>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
        />
      </label>

      <div className="space-y-2">
        <button
          type="submit"
          disabled={pending || blocked}
          className="min-h-[52px] w-full rounded-lg bg-emerald-600 px-4 py-3 text-base font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
        >
          {pending
            ? t('poolDriver.handover.sending')
            : reporting
              ? t(mode === 'checkout' ? 'poolDriver.handover.confirmCheckout' : 'poolDriver.handover.confirmCheckin')
              : t('poolDriver.handover.nothingNew')}
        </button>

        {/* Un bouton désactivé DIT pourquoi — l'irritant n°1 relevé sur l'APK. */}
        {!pending && blocked && (
          <p className="text-center text-xs text-gray-600">
            {t(!odometerReady
              ? 'poolDriver.handover.blockedOdometer'
              : 'poolDriver.handover.blockedDefect')}
          </p>
        )}

        <div className="flex flex-col gap-2 sm:flex-row-reverse">
          <button
            type="button"
            onClick={() => { setReporting((r) => !r); if (reporting) setDefect(EMPTY_DEFECT); }}
            disabled={pending}
            className="min-h-[44px] flex-1 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-60"
          >
            {t(reporting ? 'poolDriver.handover.defectDrop' : 'poolDriver.handover.defectAdd')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-3 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {t('poolDriver.handover.cancel')}
          </button>
        </div>
      </div>
    </form>
  );
}

export default HandoverForm;
